import React from 'react';
import { Input } from '../../../ui/input';
import { Label } from '../../../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../ui/select';

/**
 * Common top section of the Product Form Dialog.
 *
 * Renders brand name (with autocomplete dropdown), main category, subcategory
 * (with autocomplete dropdown), product name, grade + product-code prefix
 * (grade hidden for the "unstructured" categories), brand-description
 * (for Plywood-like categories), and the "show brand in customer PDF" toggle.
 *
 * All state and handlers come from the parent via props — this component is
 * purely presentational so the parent's mode-switching logic stays in one
 * place.
 */
export function CommonHeader({
  form,
  setForm,
  editingProduct,
  allCategories,
  brandSuggestions,
  showBrandDropdown,
  setShowBrandDropdown,
  handleBrandInput,
  selectBrand,
  subcatSuggestions,
  showSubcatDropdown,
  setShowSubcatDropdown,
  handleSubcatInput,
  selectSubcat,
  // Category-switch reset callbacks (owned by parent so all state resets
  // happen atomically when the admin changes category mid-form).
  setPlywoodSizes,
  setCustomSizes,
  setPlywoodRates,
  setCustomThicknesses,
  setAdhesiveRates,
  setAdhesiveRateTypes,
  setAdhesiveSecondaryRates,
  setCustomWeights,
}) {
  return (
    <>
      {/* Common fields: Brand and Category */}
      <div className="grid grid-cols-2 gap-3">
        {/* Brand Name with Autocomplete */}
        <div className="relative">
          <Label className="text-xs">Brand Name *</Label>
          <Input
            value={form.brand_name}
            onChange={e => handleBrandInput(e.target.value)}
            onBlur={() => setTimeout(() => setShowBrandDropdown(false), 200)}
            placeholder="Type brand name..."
            className="mt-1 text-sm"
            data-testid="form-brand-name"
          />
          {showBrandDropdown && brandSuggestions.length > 0 && (
            <div className="absolute z-50 w-full mt-1 bg-white border border-[#E2E6D9] rounded-md shadow-lg max-h-48 overflow-auto">
              {brandSuggestions.map(brand => (
                <div
                  key={brand}
                  className="px-3 py-2 text-sm cursor-pointer hover:bg-[#F2F4EF] border-b border-[#E2E6D9] last:border-0"
                  onMouseDown={() => selectBrand(brand)}
                >
                  {brand}
                </div>
              ))}
            </div>
          )}
        </div>
        <div>
          <Label className="text-xs">Category *</Label>
          {editingProduct ? (
            // Disable category change for existing products
            <div className="mt-1 px-3 py-2 text-sm bg-[#F2F4EF] border border-[#E2E6D9] rounded-md text-[#5C6150]">
              {form.main_category}
              <span className="text-[10px] text-[#8F9485] ml-2">(cannot change)</span>
            </div>
          ) : (
            <Select
              value={form.main_category}
              onValueChange={v => {
                setForm(f => ({ ...f, main_category: v, subcategory: '' }));
                // Reset ALL category-specific state when switching categories
                setPlywoodSizes([]);
                setCustomSizes([]);
                setPlywoodRates({});
                setCustomThicknesses([]);
                setAdhesiveRates({});
                setAdhesiveRateTypes({});
                setAdhesiveSecondaryRates({});
                setCustomWeights([]);
              }}
            >
              <SelectTrigger className="mt-1 text-sm" data-testid="form-category"><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent>{allCategories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
          )}
        </div>
      </div>

      {/* Common fields: Subcategory and Product Name */}
      <div className="grid grid-cols-2 gap-3">
        <div className="relative">
          <Label className="text-xs">Subcategory (e.g., CLUB PRIME) *</Label>
          <Input
            value={form.subcategory}
            onChange={e => handleSubcatInput(e.target.value)}
            onBlur={() => setTimeout(() => setShowSubcatDropdown(false), 200)}
            placeholder={form.main_category ? 'Type subcategory...' : 'Select category first'}
            className="mt-1 text-sm"
            data-testid="form-subcategory"
          />
          {showSubcatDropdown && subcatSuggestions.length > 0 && (
            <div className="absolute z-50 w-full mt-1 bg-white border border-[#E2E6D9] rounded-md shadow-lg max-h-48 overflow-auto">
              {subcatSuggestions.map(subcat => (
                <div
                  key={subcat}
                  className="px-3 py-2 text-sm cursor-pointer hover:bg-[#F2F4EF] border-b border-[#E2E6D9] last:border-0"
                  onMouseDown={() => selectSubcat(subcat)}
                >
                  {subcat}
                </div>
              ))}
            </div>
          )}
        </div>
        <div>
          <Label className="text-xs">Product Name</Label>
          <Input value={form.product_name} onChange={e => setForm(f => ({ ...f, product_name: e.target.value }))} className="mt-1 text-sm" data-testid="form-product-name" />
        </div>
      </div>

      {/* Common fields: Grade and Product Code - Hide Grade for certain categories */}
      {['Adhesives', 'Hardware', 'Screws/Patti', 'Wooden Flooring', 'Veneers', 'Highlighters', 'Laminates'].includes(form.main_category) ? (
        <div>
          <Label className="text-xs">Product Code Prefix</Label>
          <Input value={form.product_code} onChange={e => setForm(f => ({ ...f, product_code: e.target.value }))} placeholder="e.g., CP" className="mt-1 text-sm" data-testid="form-product-code" />
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-3">
          <div><Label className="text-xs">Grade</Label><Input value={form.grade} onChange={e => setForm(f => ({ ...f, grade: e.target.value }))} placeholder="e.g., BWP, MR, BWR" className="mt-1 text-sm" data-testid="form-grade" /></div>
          <div><Label className="text-xs">Product Code Prefix</Label><Input value={form.product_code} onChange={e => setForm(f => ({ ...f, product_code: e.target.value }))} placeholder="e.g., CP" className="mt-1 text-sm" data-testid="form-product-code" /></div>
        </div>
      )}

      {/* Brand Description - for Plywood, Blockboard, MDF, Doors (shown in PDF) */}
      {['Plywood', 'Blockboard', 'MDF', 'Doors', 'WPC/PVC'].includes(form.main_category) && (
        <div>
          <Label className="text-xs">Brand Description (shown in PDF)</Label>
          <Input
            value={form.brand_description || ''}
            onChange={e => setForm(f => ({ ...f, brand_description: e.target.value }))}
            placeholder="e.g., EVEREST EXCEL BWP 710 PLYWOOD (21 YEARS WARRANTY)"
            className="mt-1 text-sm"
            data-testid="form-brand-description"
          />
          <p className="text-[10px] text-muted-foreground mt-1">This description will appear as a header row in the quotation PDF</p>
        </div>
      )}

      {/* Per-product brand visibility — Admin/Manager decides whether the
          brand name appears in the CUSTOMER PDF. Defaults to ON so branded
          items (Tandem, Hettich, Hafele) stay visible; turn OFF for generic
          items like zigzag pins where the brand is meaningless to the
          customer. */}
      <div className="flex items-start gap-2 p-3 rounded-md border border-[#E2E6D9] bg-[#F9FAF7]">
        <input
          id="show-brand-in-pdf"
          type="checkbox"
          checked={form.show_brand_in_pdf !== false}
          onChange={e => setForm(f => ({ ...f, show_brand_in_pdf: e.target.checked }))}
          className="mt-0.5 w-4 h-4 accent-[#556B2F]"
          data-testid="form-show-brand-in-pdf"
        />
        <label htmlFor="show-brand-in-pdf" className="text-xs leading-tight cursor-pointer select-none">
          <span className="font-semibold text-[#1A1C16]">Show brand name in customer PDF</span>
          <p className="text-[#5C6150] text-[10px] mt-0.5">
            Turn off for generic items (e.g., zigzag pins, common screws) where the brand
            doesn&apos;t matter to the customer. Keep on for branded items like Tandem, Hettich,
            Hafele, etc.
          </p>
        </label>
      </div>
    </>
  );
}
